import {
  ApproachPerCountOptions,
  PitchType,
  PitchTypeGroup,
  PlayResult,
  UmpireCall,
  UmpireCallCorrectness,
} from 'types/CodegenReportTypes';
import { hitTypes, pitchCalls } from './constants';

const textWhite = '#FFFFFF';
const textBlack = '#414141';

export const pitchTypeToColor: Record<PitchType, string> = {
  All: '',
  Fastball: '#930412',
  Curveball: '#4097FC',
  Changeup: '#6EA200',
  Slider: '#DFB600',
  Cutter: '#614300',
  Sinker: '#F6863B',
  Splitter: '#00798C',
  Knuckleball: '#8B4CDC',
  FourSeam: '#F30624',
  Undefined: '#939393',
} as const;

export const pitchTypeToTextColor: Record<PitchType, string> = {
  All: '',
  Fastball: textWhite,
  Curveball: textBlack,
  Changeup: textWhite,
  Slider: textBlack,
  Cutter: textWhite,
  Sinker: textBlack,
  Splitter: textBlack,
  Knuckleball: textWhite,
  FourSeam: textWhite,
  Undefined: textBlack,
} as const;

export const pitchTypeGroupToColor: Record<PitchTypeGroup, string> = {
  All: '',
  Fastballs: '#5D2CA8',
  Offspeed: '#547700',
  Breaking: '#D3045C',
  Undefined: '#939393',
} as const;

export const pitchTypeGroupTextToColor: Record<PitchTypeGroup, string> = {
  All: '',
  Fastballs: textWhite,
  Offspeed: textWhite,
  Breaking: textWhite,
  Undefined: textBlack,
} as const;

export const approachToColor: Record<ApproachPerCountOptions, string> = {
  Take: '#547700',
  Swing: '#00798C',
} as const;

export const playResultToColor: Record<PlayResult, string> = {
  Single: '#00D0EC',
  Double: '#FEA292',
  Triple: '#FDCE00',
  HomeRun: '#FF9CCD',
  Out: '#939393',
} as const;

export const playResultToTextColor: Record<PlayResult, string> = {
  Single: textBlack,
  Double: textBlack,
  Triple: textBlack,
  HomeRun: textBlack,
  Out: textBlack,
} as const;

export const pitchCallToColor: Record<(typeof pitchCalls)[number], string> = {
  BallCalled: '#F30624',
  FoulBall: '#F08C00',
  FoulBallFieldable: '#F08C00',
  FoulBallNotFieldable: '#F08C00',
  HitByPitch: '#FFD700',
  InPlay: '#6A0DAD',
  StrikeCalled: '#10AB03',
  StrikeSwinging: '#0056B3',
} as const;

export const pitchCallToTextColor: Record<(typeof pitchCalls)[number], string> = {
  BallCalled: textWhite,
  FoulBall: textWhite,
  FoulBallFieldable: textWhite,
  FoulBallNotFieldable: textWhite,
  HitByPitch: textBlack,
  InPlay: textWhite,
  StrikeCalled: textWhite,
  StrikeSwinging: textWhite,
} as const;

export const umpireCallToColor: Record<UmpireCall, string> = {
  All: '',
  StrikeCalled: '#235196',
  BallCalled: '#08AB00',
} as const;

export const umpireCallToTextColor: Record<UmpireCall, string> = {
  All: '',
  StrikeCalled: textWhite,
  BallCalled: textBlack,
} as const;

export const umpireCallCorrectnessToColor: Record<UmpireCallCorrectness, string> = {
  BufferZone: '#DFB600',
  Correct: '#08AB00',
  Incorrect: '#F30624',
} as const;

export const umpireCallCorrectnessToTextColor: Record<UmpireCallCorrectness, string> = {
  BufferZone: textBlack,
  Correct: textBlack,
  Incorrect: textWhite,
} as const;

export const hitTypeToColor: Record<(typeof hitTypes)[number], string> = {
  Bunt: '#8F0059',
  FlyBall: '#2264FB',
  GroundBall: '#FE5085',
  LineDrive: '#00A875',
  NoSwing: '#8A6600',
  PopUp: '#B27BEC',
  Undefined: '#939393',
} as const;

export const hitTypeToTextColor: Record<(typeof hitTypes)[number], string> = {
  Bunt: textWhite,
  FlyBall: textWhite,
  GroundBall: textBlack,
  LineDrive: textWhite,
  NoSwing: textWhite,
  PopUp: textBlack,
  Undefined: textBlack,
} as const;

export const orderedColorSets = [
  { bg: '#235196', text: textWhite }, // lowest value
  { bg: '#3270bf', text: textWhite },
  { bg: '#458fe3', text: textWhite },
  { bg: '#5eaefa', text: textBlack },
  { bg: '#95cdfc', text: textBlack },
  { bg: '#c4e5fd', text: textBlack },
  { bg: '#e9f6fe', text: textBlack },
  { bg: '#fdf2f1', text: textBlack },
  { bg: '#f8d9da', text: textBlack },
  { bg: '#f3b7b9', text: textBlack },
  { bg: '#ee8b8c', text: textBlack },
  { bg: '#e65c5b', text: textWhite },
  { bg: '#c73731', text: textWhite },
  { bg: '#942a25', text: textWhite }, // highest value
] as const;

export const getOrderedColorSet = (min: number, max: number, value: number) => {
  if (min === 0 && max === 0 && value === 0) return { bg: '#FFFFFF', text: textBlack };
  if (value <= min) return orderedColorSets[0];
  if (value >= max) return orderedColorSets[orderedColorSets.length - 1];

  const range = max - min;
  const step = range / (orderedColorSets.length - 1);
  const index = Math.round((value - min) / step);

  return orderedColorSets[index];
};
